<script lang="ts" setup>
import DsButton from '@/components/design-system/form/input/DsButton.vue';
import type { AccountApiResponse } from '@/employee-portal/accounts/Account.types';
import DeactivateAccountModal from '@/employee-portal/accounts/DeactivateAccountModal.vue';
import { useCurrentUserStore } from '@/stores/current-user.store';
import { useShadowAccountStore } from '@/stores/shared/shadow-account.store';
import PvMenu from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { computed, ref } from 'vue';
import { RouterLink } from 'vue-router';

const props = defineProps<Pick<AccountApiResponse['account'], 'id' | 'name'>>();
const { currentUserMeta } = useCurrentUserStore();
const { startShadowing } = useShadowAccountStore();

const menu = ref();
const showDeactivateModal = ref(false);
const toggleAccountOptions = (event: MouseEvent) => {
    menu.value.toggle(event);
};

const companyAccountActionsOptions = computed(() => {
    const items: MenuItem[] = [
        {
            id: 'enable',
            label: 'Enable Payments',
            to: `/accounts/${props.id}/payment-settings`,
        },
        {
            id: 'enable-2wt',
            label: 'Enable Two-Way Texting',
            to: `/accounts/${props.id}/two-way-texting`,
        },
        {
            id: 'enable-progress-tracker',
            label: 'Enable Progress Tracker',
            to: `/accounts/${props.id}/progress-tracker`,
        },
    ];
    if (props.id !== currentUserMeta.userAccountId) {
        items.push({
            id: 'shadow',
            label: 'Shadow Account',
            command: () => {
                startShadowing(props.id);
            },
        });
    }
    items.push({
        id: 'deactivate',
        label: 'Deactivate Account',
        danger: true,
        command: () => {
            showDeactivateModal.value = true;
        },
    });
    return [{ items }];
});
</script>

<template>
    <DeactivateAccountModal
        v-bind="props"
        v-model="showDeactivateModal"
    />
    <DsButton
        size="md"
        trailing="fas fa-chevron-down"
        variant="secondary"
        @click="toggleAccountOptions"
    >
        Actions
    </DsButton>
    <PvMenu
        ref="menu"
        :model="companyAccountActionsOptions"
        popup
    >
        <template #item="{ item, props }">
            <RouterLink
                v-if="item.to"
                v-bind="props.action"
                class="flex no-underline"
                :to="item.to"
            >
                <span :class="item.icon" />
                <span
                    class="no-underline"
                    :class="{ 'text-ds-red-600': item.danger }"
                >
                    {{ item.label }}
                </span>
            </RouterLink>
            <a
                v-else
                class="flex no-underline"
                v-bind="props.action"
                :href="item.url"
            >
                <span :class="item.icon" />
                <span
                    class="no-underline"
                    :class="{ 'text-ds-red-600': item.danger }"
                >
                    {{ item.label }}
                </span>
            </a>
        </template>
    </PvMenu>
</template>
